import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Trigger, Tag, Space, Empty, Spin, Typography, Tooltip, Message,
} from '@arco-design/web-react';
import {
  IconCheckCircle, IconClockCircle, IconExclamationCircle, IconLink,
  IconCheckSquare,
} from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

const { Text } = Typography;

const STATUS_ICON = {
  in_progress: <IconClockCircle style={{ color: '#165DFF', fontSize: 14 }} />,
  blocked: <IconExclamationCircle style={{ color: '#F53F3F', fontSize: 14 }} />,
  pending: <IconClockCircle style={{ color: '#86909C', fontSize: 14 }} />,
};

const PRIORITY_COLORS = {
  urgent: 'red',
  high: 'orangered',
  medium: 'blue',
  low: 'gray',
};

const TaskBell = () => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);

  const { data: countData } = useQuery({
    queryKey: ['system', 'my-tasks', 'count'],
    queryFn: async () => {
      const resp = await apiClient.get(API_ENDPOINTS.SYSTEM_PROC_INSTANCES_MY_TASKS_COUNT);
      return resp.data;
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['system', 'my-tasks', 'list'],
    queryFn: async () => {
      const resp = await apiClient.get(API_ENDPOINTS.SYSTEM_PROC_INSTANCES_MY_TASKS);
      return resp.data;
    },
    enabled: visible,
    staleTime: 30000,
  });

  const completeMutation = useMutation({
    mutationFn: async ({ instanceId, stepLogId }) =>
      apiClient.put(API_ENDPOINTS.SYSTEM_PROC_INSTANCES_UPDATE_STEP,
        { instance_id: instanceId, step_log_id: stepLogId },
        { status: 'completed' }
      ),
    onSuccess: () => {
      Message.success('Paso completado');
      queryClient.invalidateQueries(['system', 'my-tasks']);
      queryClient.invalidateQueries(['system', 'procedure-instances']);
    },
    onError: () => Message.error('Error al completar'),
  });

  const taskCount = countData?.count || 0;
  const tasks = tasksData?.tasks || [];

  const popupContent = (
    <div style={{ width: 380, maxHeight: 480, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F2F3F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text bold style={{ fontSize: 14 }}>Mis tareas</Text>
        <Button size="mini" type="text" onClick={() => { setVisible(false); navigate('/hub/tareas'); }}>
          Ver todo
        </Button>
      </div>

      {/* Task list */}
      <div style={{ maxHeight: 380, overflow: 'auto', padding: '4px 0' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin size={24} /></div>
        ) : tasks.length === 0 ? (
          <Empty description="Sin tareas pendientes" style={{ padding: 40 }} />
        ) : (
          tasks.slice(0, 10).map((task) => (
            <div
              key={task.id}
              style={{
                padding: '10px 16px', borderBottom: '1px solid #F7F8FA',
                display: 'flex', gap: 10, alignItems: 'flex-start',
                cursor: 'pointer', transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F7F8FA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Status icon */}
              <div style={{ paddingTop: 2 }}>{STATUS_ICON[task.status] || STATUS_ICON.pending}</div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text bold style={{ fontSize: 12, display: 'block', lineHeight: '18px' }}>
                  {task.process_name}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                  {task.procedure_code} — {task.instance_title}
                </Text>
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <Tag size="small" color={PRIORITY_COLORS[task.priority]}>{task.priority}</Tag>
                  <Tag size="small" color={task.status === 'in_progress' ? 'blue' : task.status === 'blocked' ? 'red' : 'gray'}>
                    {task.status === 'in_progress' ? 'En progreso' : task.status === 'blocked' ? 'Bloqueado' : 'Pendiente'}
                  </Tag>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {task.status === 'in_progress' && (
                  <Tooltip content="Marcar como completado">
                    <Button size="mini" type="outline" status="success"
                      icon={<IconCheckCircle />}
                      onClick={(e) => {
                        e.stopPropagation();
                        completeMutation.mutate({ instanceId: task.instance_id, stepLogId: task.id });
                      }}
                    />
                  </Tooltip>
                )}
                {task.page_route && (
                  <Tooltip content="Ir a la página">
                    <Button size="mini" type="outline"
                      icon={<IconLink />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisible(false);
                        navigate(task.page_route);
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {tasks.length > 10 && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid #F2F3F5', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            +{tasks.length - 10} más...
          </Text>
        </div>
      )}
    </div>
  );

  return (
    <Trigger
      trigger="click"
      popup={() => popupContent}
      popupVisible={visible}
      onVisibleChange={setVisible}
      position="br"
      popupAlign={{ bottom: 8 }}
    >
      <Badge count={taskCount} dot={taskCount > 0}>
        <Button
          type="text"
          icon={<IconCheckSquare />}
          style={{ color: taskCount > 0 ? '#165DFF' : undefined }}
        />
      </Badge>
    </Trigger>
  );
};

export default TaskBell;
